import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Ruler, Plus, Loader2, Trash2, ChevronRight, FileText, Sparkles } from "lucide-react";

/**
 * /takeoffs — Top-level list of every quantity takeoff the workspace has
 * created.  Includes both private (non-federal) jobs and any takeoffs
 * generated off a SAM.gov opportunity.
 */
export default function Takeoffs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    project_name: "",
    address: "",
    client_name: "",
    scope_summary: "",
    trades_input: "all",
    tapered_system: false,
  });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/takeoffs");
      setItems(r.data.items || []);
    } catch (err) {
      console.error("Takeoffs list failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.project_name.trim()) {
      toast.error("Give the project a name first");
      return;
    }
    setCreating(true);
    try {
      const trades =
        form.trades_input.trim().toLowerCase() === "all"
          ? ["all"]
          : form.trades_input.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
      const r = await api.post("/takeoffs", {
        project_name: form.project_name.trim(),
        address: form.address.trim() || null,
        client_name: form.client_name.trim() || null,
        scope_summary: form.scope_summary.trim() || null,
        source_type: "private",
        trades,
        tapered_system: !!form.tapered_system,
      });
      toast.success(`Created "${r.data.project_name}"`);
      navigate(`/takeoffs/${r.data.takeoff_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create takeoff");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/takeoffs/${id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="takeoffs-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <Ruler className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-orange-600">// QUANTITY TAKEOFFS</div>
            <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900">Takeoffs</h1>
          </div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="bf-pill text-xs px-5 py-2.5 inline-flex items-center gap-2"
          data-testid="takeoffs-new-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          NEW TAKEOFF
        </button>
      </div>

      <p className="text-slate-600 max-w-3xl mb-8">
        Structured quantity takeoffs for private / non-federal jobs — plus every takeoff you've generated from a
        SAM.gov opportunity. Upload plans, tell the Takeoff Agent which trades to cover, and it delivers editable
        tables with waste factors, linear measurements, assumptions, and a printable PDF that matches your house
        format.
      </p>

      {showNew && (
        <div
          className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 mb-8"
          data-testid="takeoffs-new-form"
        >
          <div className="text-[10px] font-mono font-bold tracking-widest text-orange-600 mb-4">
            // NEW PRIVATE-JOB TAKEOFF
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1 block">
                PROJECT NAME
              </label>
              <input
                value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                placeholder="3601 Moon River Rd — Reroof + Addition"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                data-testid="takeoffs-project-name"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1 block">
                ADDRESS
              </label>
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Austin, TX"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                data-testid="takeoffs-address"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1 block">
                CLIENT / ARCHITECT
              </label>
              <input
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="Katie Hastings Architecture"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                data-testid="takeoffs-client"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1 block">
                TRADES (comma-separated, or "all")
              </label>
              <input
                value={form.trades_input}
                onChange={(e) => setForm({ ...form, trades_input: e.target.value })}
                placeholder="roofing, framing, siding"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                data-testid="takeoffs-trades"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1 block">
              SCOPE SUMMARY (optional — sharpens the AI's first pass)
            </label>
            <textarea
              value={form.scope_summary}
              onChange={(e) => setForm({ ...form, scope_summary: e.target.value })}
              rows={3}
              placeholder="Existing roof re-roof + 285 SF L3 addition, 7:12 pitch main planes. Two shingle-vs-slate options."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              data-testid="takeoffs-scope"
            />
          </div>
          <label
            className="mb-4 flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/40 cursor-pointer transition-colors"
            data-testid="takeoffs-tapered-label"
          >
            <input
              type="checkbox"
              checked={form.tapered_system}
              onChange={(e) => setForm({ ...form, tapered_system: e.target.checked })}
              className="mt-0.5 accent-cyan-500 w-4 h-4"
              data-testid="takeoffs-tapered-checkbox"
            />
            <span>
              <span className="block text-sm font-medium text-slate-900">Tapered insulation system</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Check for low-slope TPO / EPDM / PVC roofs with tapered polyiso. Runs an extra pass that quantifies tapered field area, average thickness, board-feet, crickets/saddles, and minimum R-value at the thin point.
              </span>
            </span>
          </label>
          <div className="flex gap-2">
            <button
              onClick={create}
              disabled={creating}
              className="bf-pill text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="takeoffs-create-submit"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {creating ? "CREATING…" : "CREATE TAKEOFF"}
            </button>
            <button
              onClick={() => setShowNew(false)}
              className="text-xs px-4 py-2.5 rounded-full font-mono font-bold tracking-widest text-slate-600 hover:bg-slate-100"
              data-testid="takeoffs-create-cancel"
            >
              CANCEL
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-slate-500 text-sm">
          <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin" /> Loading takeoffs…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-16 text-center"
          data-testid="takeoffs-empty"
        >
          <Ruler className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <div className="font-display font-bold text-lg text-slate-900 mb-1">No takeoffs yet</div>
          <div className="text-sm text-slate-500 mb-6">
            Create your first takeoff — upload plans, and BidForge builds the quantities.
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2"
            data-testid="takeoffs-empty-new"
          >
            <Plus className="w-3.5 h-3.5" />
            START A TAKEOFF
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="takeoffs-list">
          {items.map((t) => (
            <div
              key={t.takeoff_id}
              className="group rounded-xl bg-white border border-slate-200 hover:border-orange-400 hover:shadow-md transition-all p-4 flex items-center gap-4"
              data-testid={`takeoff-row-${t.takeoff_id}`}
            >
              <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <Link to={`/takeoffs/${t.takeoff_id}`} className="flex-1 min-w-0" data-testid={`takeoff-open-${t.takeoff_id}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="font-display font-bold text-slate-900 truncate">{t.project_name}</div>
                  {t.source_type === "federal" && (
                    <span className="text-[9px] font-mono font-bold tracking-widest px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">
                      FEDERAL
                    </span>
                  )}
                  {t.source_type === "private" && (
                    <span className="text-[9px] font-mono font-bold tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                      PRIVATE
                    </span>
                  )}
                  <span
                    className={`text-[9px] font-mono font-bold tracking-widest px-1.5 py-0.5 rounded-full border ${
                      t.status === "generated"
                        ? "bg-green-100 text-green-700 border-green-200"
                        : t.status === "edited"
                        ? "bg-blue-100 text-blue-700 border-blue-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}
                  >
                    {t.status?.toUpperCase() || "DRAFT"}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {[t.address, t.client_name, `${(t.trades || []).join(", ") || "all trades"}`]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </Link>
              <div className="text-xs text-slate-400 font-mono">
                {new Date(t.updated_at).toLocaleDateString()}
              </div>
              <button
                onClick={() => remove(t.takeoff_id, t.project_name)}
                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                data-testid={`takeoff-delete-${t.takeoff_id}`}
                title="Delete takeoff"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
